import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Keyboard, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import KeypadModal from '../components/KeypadModal';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';
import { toast } from '../hooks/use-toast';

const Retrieve = () => {
  const navigate = useNavigate();
  const { lockers, whiteLabel, retrieveParcel, currentUser, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const t = (key) => getTranslation(key, language);
  
  const [showKeypad, setShowKeypad] = useState(false);
  const [method, setMethod] = useState(null);
  const [retrievedLocker, setRetrievedLocker] = useState(null);
  const [success, setSuccess] = useState(false);

  // Filter parcels destined to current user
  const myParcels = lockers.filter(l => 
    l.state === 'Occupied' && 
    l.recipient === currentUser?.name
  );

  const handlePinSubmit = (pin) => {
    const locker = myParcels.find(l => l.pin === pin);
    if (locker) {
      retrieveParcel(locker.id, 'PIN', currentUser?.name);
      setRetrievedLocker(locker);
      setSuccess(true);
      toast({
        title: t('retrievalSuccessful'),
        description: `Locker #${locker.id} opened`,
      });
    } else {
      toast({
        title: t('invalidPin'),
        description: t('pinDoesNotMatch'),
        variant: 'destructive'
      });
    }
    setShowKeypad(false);
  };

  const handleRfidSimulation = () => {
    if (myParcels.length === 0) {
      toast({
        title: t('noParcels'),
        description: t('noParcelToRetrieve'),
        variant: 'destructive'
      });
      return;
    }
    
    // RFID retrieves the first parcel for current user
    const locker = myParcels[0];
    retrieveParcel(locker.id, 'RFID', currentUser?.name);
    setRetrievedLocker(locker);
    setSuccess(true);
    toast({
      title: t('retrievalSuccessful'),
      description: `Locker #${locker.id} ${t('openedViaRfid')}`,
    });
  };

  const handleReset = () => {
    setSuccess(false);
    setRetrievedLocker(null);
    setMethod(null);
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle size={64} className="text-green-600" />
            </div>
            <CardTitle className="text-center text-2xl">{t('lockerOpened')}!</CardTitle>
            <CardDescription className="text-center">{t('collectParcel')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-6 rounded-xl bg-green-50 border-2 border-green-200 text-center">
              <p className="text-sm text-gray-600">{t('lockerNumber')}</p>
              <p className="text-5xl font-bold text-[#1A1A1A] my-2">#{retrievedLocker?.id}</p>
              <p className="text-sm text-gray-600">{t('size')}: {retrievedLocker?.sizeCode}</p>
              {retrievedLocker?.user && (
                <p className="text-sm font-semibold text-gray-700 mt-2">
                  {t('exp')}: {retrievedLocker.user}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Button 
                onClick={() => navigate('/lockers')}
                className="w-full"
                style={{ backgroundColor: primaryColor }}
              >
                {t('viewMap')}
              </Button>
              <Button 
                variant="outline"
                onClick={handleReset}
                className="w-full"
              >
                {t('retrieveAnother')}
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                {t('backToHome')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-[#1A1A1A]">{t('receiveParcel')}</h1>
        <p className="text-gray-600">{t('chooseAuthMethod')}</p>
        <p className="text-sm text-gray-500">
          {myParcels.length} {t('parcel')}{myParcels.length !== 1 ? 's' : ''} {t('forYou')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card 
          className="cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1 border-2"
          onClick={() => {
            setMethod('PIN');
            setShowKeypad(true);
          }}
          style={{ borderColor: primaryColor }}
        >
          <CardHeader>
            <div className="flex items-center justify-center w-20 h-20 rounded-full mb-4 mx-auto" style={{ backgroundColor: `${primaryColor}20` }}>
              <Keyboard size={40} style={{ color: primaryColor }} />
            </div>
            <CardTitle className="text-2xl text-center">{t('enterPin')}</CardTitle>
            <CardDescription className="text-center">
              {t('usePinCode')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full text-lg py-6"
              style={{ backgroundColor: primaryColor }}
            >
              {t('openKeypad')}
            </Button>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1 border-2"
          onClick={handleRfidSimulation}
          style={{ borderColor: primaryColor }}
        >
          <CardHeader>
            <div className="flex items-center justify-center w-20 h-20 rounded-full mb-4 mx-auto" style={{ backgroundColor: `${primaryColor}20` }}>
              <CreditCard size={40} style={{ color: primaryColor }} />
            </div>
            <CardTitle className="text-2xl text-center">{t('rfidBadge')}</CardTitle>
            <CardDescription className="text-center">
              {t('tapBadge')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full text-lg py-6"
              style={{ backgroundColor: primaryColor }}
            >
              {t('simulateRfid')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('myParcels')}</CardTitle>
          <CardDescription>{t('parcelsForYou')}</CardDescription>
        </CardHeader>
        <CardContent>
          {myParcels.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {myParcels.map(locker => (
                <div 
                  key={locker.id}
                  className="p-4 rounded-lg border-2 text-left"
                  style={{ borderColor: primaryColor, backgroundColor: `${primaryColor}10` }}
                >
                  <div className="text-2xl font-bold mb-1">#{locker.id}</div>
                  <div className="text-sm text-gray-600 mb-1">{t('size')}: {locker.sizeCode}</div>
                  {locker.user && (
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      {t('exp')}: {locker.user}
                    </div>
                  )}
                  {locker.pin && (
                    <div className="text-lg font-bold mt-2" style={{ color: primaryColor }}>
                      PIN: {locker.pin}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">{t('noParcelForYou')}</p>
          )}
        </CardContent>
      </Card>

      <KeypadModal 
        isOpen={showKeypad}
        onClose={() => setShowKeypad(false)}
        onSubmit={handlePinSubmit}
        title={t('enterYourPin')}
      />
    </div>
  );
};

export default Retrieve;
